/**
 * ZO Bull Algo Trading Platform
 * Version: 6.2.0
 */
/**
 * AI Copilot Chat Component
 * 
 * A floating chat widget for ZO Bull that provides:
 * - Real-time trading assistance
 * - Strategy building help
 * - Multi-language support (42 languages)
 * - Persistent chat history
 * - Streaming responses
 * - Context-aware conversations
 */

import React, { useState, useRef, useEffect } from 'react';
import './CopilotChat.css';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

interface ChatContext {
  exchange?: string;
  strategy?: string;
  indicators?: string[];
}

// Storage key for chat history
const STORAGE_KEY = 'zobull_copilot_history';
const CONTEXT_KEY = 'zobull_copilot_context';

// Trading-related knowledge base for offline fallback
const TRADING_FAQ: Record<string, string> = {
  'rsi': 'RSI (Relative Strength Index) is a momentum oscillator (0-100) measuring speed of price changes. RSI > 70 = Overbought, RSI < 30 = Oversold. Common settings: 14 periods.',
  'ema': 'EMA (Exponential Moving Average) gives more weight to recent prices. EMA 9/21 for short-term, 50/200 for long-term trends. Crossover strategies are popular.',
  'supertrend': 'Supertrend is a trend-following indicator using ATR. Green = Uptrend (Buy), Red = Downtrend (Sell). Settings: Period 10, Multiplier 3 is common.',
  'macd': 'MACD (Moving Average Convergence Divergence) shows relationship between two EMAs. Signal line crossovers generate buy/sell signals. Histogram shows momentum.',
  'bollinger': 'Bollinger Bands show price volatility with upper/lower bands. Price touching bands can indicate reversals. Squeeze indicates low volatility breakout.',
  'sl': 'Stop Loss (SL) limits your maximum loss per trade. Recommended: 1-5% for options, 2-5% for futures. Always set SL before entering a trade.',
  'tp': 'Take Profit (TP) locks in profits at target levels. Risk:Reward ratio of 1:2 or better is recommended. Can use trailing SL for locked profits.',
  'ce': 'Call Option (CE) profits when price goes up. Buy CE when bullish, Sell CE when bearish (covered). Strike selection: ATM for premiums, OTM for directional bets.',
  'pe': 'Put Option (PE) profits when price goes down. Buy PE when bearish, Sell PE when bullish (covered). Used for hedging or directional trades.',
  'atm': 'ATM (At-The-Money) options have strike price equal to current price. Highest gamma, most responsive to price moves. Good for volatility trades.',
  'itm': 'ITM (In-The-Money) options have intrinsic value. Lower leverage but more stable. Good for hedging or capital preservation.',
  'otm': 'OTM (Out-Of-The-Money) options are cheaper but need larger moves. Higher leverage, higher risk/reward. Popular for speculative trades.',
  'straddle': 'Straddle = Buy CE + PE at same strike. Profits from big moves in either direction. Best before events with high volatility expected.',
  'strangle': 'Strangle = Buy OTM CE + OTM PE. Cheaper than straddle, needs bigger move. Wider breakeven but lower cost.',
  'iv': 'IV (Implied Volatility) shows market expectation of price swings. High IV = expensive options. IV Crush happens after events (earnings).',
  'delta': 'Delta measures option price sensitivity to underlying. Call delta 0 to 1, Put delta 0 to -1. ATM delta ~0.5. Used for hedging.',
  'gamma': 'Gamma measures rate of delta change. Highest for ATM options near expiry. Important for portfolio hedging strategies.',
  'theta': 'Theta is time decay - options lose value daily. Sell theta to profit from decay. ATM options have highest theta.',
  'vega': 'Vega measures sensitivity to volatility. Long options = positive vega. Volatility crush hurts long positions.',
};

const CopilotChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [context, setContext] = useState<ChatContext>({});
  const [isMobile, setIsMobile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load chat history on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem(STORAGE_KEY);
    const savedContext = localStorage.getItem(CONTEXT_KEY);
    
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed.map((m: Message) => ({
          ...m,
          timestamp: new Date(m.timestamp)
        })));
      } catch (e) {
        console.error('Failed to load chat history:', e);
      }
    } else {
      // Initial welcome message
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: '👋 Welcome to ZO Bull AI Copilot!\n\nI can help you with:\n• Trading strategies (42 languages supported)\n• Indicator explanations\n• Risk management tips\n• Market analysis\n\nTry: "Explain RSI" or "Supertrend kya hai?"',
        timestamp: new Date()
      }]);
    }
    
    if (savedContext) {
      try {
        setContext(JSON.parse(savedContext));
      } catch (e) {
        console.error('Failed to load context:', e);
      }
    }
  }, []);

  // Save chat history
  useEffect(() => {
    if (messages.length > 1) { // Don't save just the welcome message
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    }
  }, [messages]);

  // Save context
  useEffect(() => {
    if (Object.keys(context).length > 0) {
      localStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
    }
  }, [context]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Generate unique ID
  const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Check for FAQ queries
  const checkFAQ = (query: string): string | null => {
    const lowerQuery = query.toLowerCase();
    
    for (const [key, answer] of Object.entries(TRADING_FAQ)) {
      if (lowerQuery.includes(key)) {
        return answer;
      }
    }
    return null;
  };

  // Parse natural language to strategy
  const parseStrategyQuery = (query: string): { isStrategy: boolean; response: string } => {
    const lowerQuery = query.toLowerCase();
    
    // Check for strategy-related keywords
    const strategyKeywords = ['buy', 'sell', 'when', 'cross', 'rsi', 'ema', 'supertrend', 'macd', 'stoploss', 'target', 'sl', 'tp'];
    const hasStrategyKeywords = strategyKeywords.some(kw => lowerQuery.includes(kw));
    
    if (hasStrategyKeywords) {
      // Check for specific patterns
      if (lowerQuery.includes('rsi') && (lowerQuery.includes('cross') || lowerQuery.includes('70') || lowerQuery.includes('30'))) {
        return {
          isStrategy: true,
          response: '📊 **RSI Strategy Detected!**\n\nI can create this strategy:\n• **Entry**: Buy when RSI crosses above 70 (overbought) for reversal\n• **Exit**: Sell when RSI crosses below 30 (oversold)\n• **Alternative**: Buy PE when RSI > 70 (continuation)\n\nWould you like me to create this strategy? Just say "Create RSI Reversal Strategy"'
        };
      }
      
      if (lowerQuery.includes('supertrend')) {
        return {
          isStrategy: true,
          response: '📈 **Supertrend Strategy Detected!**\n\nI can create:\n• **Bullish**: Buy when Supertrend turns green\n• **Bearish**: Sell when Supertrend turns red\n• **Combined**: Buy CE when bullish + ADX > 25\n\nExample: "Buy NIFTY CE when Supertrend is bullish with SL 5% TP 15%"\n\nShall I create this strategy?'
        };
      }
      
      if (lowerQuery.includes('ema') || lowerQuery.includes('cross')) {
        return {
          isStrategy: true,
          response: '📉 **EMA Crossover Strategy Detected!**\n\nI can create:\n• **Golden Cross**: Buy when EMA 20 crosses above EMA 50\n• **Death Cross**: Sell when EMA 20 crosses below EMA 50\n• **Multiple EMA**: Buy when EMA 9 > EMA 21 > EMA 50\n\nShall I create this strategy?'
        };
      }
      
      return {
        isStrategy: true,
        response: '✅ **Strategy Query Detected!**\n\nI understand you want to create a trading strategy. Please provide more details:\n\n1. **What to trade?** (NIFTY CE, BANKNIFTY PE, BTC, etc.)\n2. **Entry condition?** (RSI > 70, EMA cross, etc.)\n3. **Risk management?** (SL %, TP %)\n\nOr try: "Create a momentum strategy for NIFTY"'
      };
    }
    
    return { isStrategy: false, response: '' };
  };

  // Process user message
  const processMessage = async (userMessage: string): Promise<string> => {
    // Check FAQ first (offline)
    const faqAnswer = checkFAQ(userMessage);
    if (faqAnswer) {
      return faqAnswer;
    }
    
    // Check for strategy parsing
    const strategyResult = parseStrategyQuery(userMessage);
    if (strategyResult.isStrategy) {
      return strategyResult.response;
    }
    
    // Try API call with streaming fallback
    try {
      const response = await fetch('/api/ai-copilot/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          context: context
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.message || data.response || data.content;
      }
    } catch (error) {
      console.log('API not available, using offline mode');
    }
    
    // Fallback intelligent responses
    return generateOfflineResponse(userMessage);
  };

  // Generate offline response
  const generateOfflineResponse = (query: string): string => {
    const lowerQuery = query.toLowerCase();
    
    // Greetings
    if (lowerQuery.match(/^(hi|hello|hey|namaste|namaskar)/)) {
      return '🙏 Namaste! Welcome to ZO Bull AI Copilot!\n\nI can help you with:\n• Trading strategies in 42 languages\n• Technical indicators explained\n• Risk management tips\n• Market insights\n\nWhat would you like to learn about?';
    }
    
    // Help requests
    if (lowerQuery.includes('help') || lowerQuery.includes('kaise') || lowerQuery.includes('kya')) {
      return '🆘 Here\'s what I can help with:\n\n**Strategies:**\n• "Create RSI strategy for NIFTY"\n• "Supertrend buy signal kaise use karein"\n\n**Indicators:**\n• "Explain MACD"\n• "EMA kya hai?"\n\n**Risk Management:**\n• "Stop loss kaise set karein"\n• "Position sizing kya hai"\n\n**Market Analysis:**\n• "What is IV crush?"\n• "Put Call Ratio kya dikhata hai"\n\nTry asking in English, Hindi, or Hinglish!';
    }
    
    // Strategy creation requests
    if (lowerQuery.includes('create') || lowerQuery.includes('banao') || lowerQuery.includes('banana')) {
      return '📝 **Strategy Creation Mode**\n\nTell me your strategy in plain language:\n\n**English:** "Buy NIFTY CE when RSI crosses above 70 with SL 5%"\n\n**Hinglish:** "RSI 70 ke upar jaye toh NIFTY CE kharido SL 5% ke saath"\n\n**Hindi:** "Supertrend ziel racha toh option kharidna"\n\nInclude: Entry condition, Exit (SL/TP), Quantity';
    }
    
    // Market hours
    if (lowerQuery.includes('market') && (lowerQuery.includes('time') || lowerQuery.includes('hour') || lowerQuery.includes('time'))) {
      return '🕐 **Market Timings (IST)**\n\n**Indian Markets:**\n• Pre-Open: 9:00 AM - 9:15 AM\n• Regular: 9:15 AM - 3:30 PM\n• Post-Close: 3:30 PM - 4:00 PM\n\n**Crypto Markets:**\n• Open 24/7 (global markets)\n\n**Best Trading Times:**\n• Intraday: 9:15-11:00 AM (high volatility)\n• Swing: After 3:00 PM (trend confirmation)\n• Options: Last 30 mins (high premium decay)';
    }
    
    // Default response
    return '🤔 I\'m not sure I understand. Try asking:\n\n• "Explain RSI"\n• "Supertrend kya hai?"\n• "Create a momentum strategy"\n• "Market timing kya hai?"\n\nI support 42 languages! 🇺🇸🇮🇳🇪🇸🇨🇳🇯🇵';
  };

  // Handle send message
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    
    // Add streaming assistant message
    const assistantMessage: Message = {
      id: generateId(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };
    setMessages(prev => [...prev, assistantMessage]);
    
    try {
      // Try streaming endpoint first
      const response = await fetch('/api/ai-copilot/stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          context: context
        }),
      });
      
      if (response.ok && response.body) {
        // Handle streaming response
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          fullContent += chunk;
          
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessage.id 
              ? { ...msg, content: fullContent }
              : msg
          ));
        }
        
        setMessages(prev => prev.map(msg => 
          msg.id === assistantMessage.id 
            ? { ...msg, content: fullContent, isStreaming: false }
            : msg
        ));
      } else {
        // Fall back to non-streaming
        const responseText = await processMessage(userMessage.content);
        
        setMessages(prev => prev.map(msg => 
          msg.id === assistantMessage.id 
            ? { ...msg, content: responseText, isStreaming: false }
            : msg
        ));
      }
    } catch (error) {
      console.error('Chat error:', error);
      
      // Use offline fallback
      const responseText = await processMessage(userMessage.content);
      
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessage.id 
          ? { ...msg, content: responseText, isStreaming: false }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Clear chat
  const clearChat = () => {
    setMessages([{
      id: generateId(),
      role: 'assistant',
      content: '🗑️ Chat cleared! Starting fresh.\n\nHow can I help you with trading today?',
      timestamp: new Date()
    }]);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Format timestamp
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-IN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Quick action definitions
  const quickActions = [
    { id: 'strategy', icon: '📊', label: 'Strategy', action: () => { setInputValue('Create a new strategy'); setIsOpen(true); setIsExpanded(false); } },
    { id: 'backtest', icon: '🔬', label: 'Backtest', action: () => { window.location.href = '/strategies?tab=backtest'; setIsExpanded(false); } },
    { id: 'broker', icon: '🔗', label: 'Broker', action: () => { window.location.href = '/brokers'; setIsExpanded(false); } },
    { id: 'paper', icon: '📝', label: 'Paper', action: () => { window.location.href = '/strategies?tab=paper'; setIsExpanded(false); } },
    { id: 'analytics', icon: '📈', label: 'Analytics', action: () => { window.location.href = '/analytics'; setIsExpanded(false); } },
    { id: 'help', icon: '❓', label: 'Help', action: () => { setInputValue('Help me get started'); setIsOpen(true); setIsExpanded(false); } },
    { id: 'patterns', icon: '🕯️', label: 'Patterns', action: () => { setInputValue('Show me candlestick patterns'); setIsOpen(true); setIsExpanded(false); } },
    { id: 'options', icon: '📊', label: 'Options', action: () => { setInputValue('Explain PCR and Greeks'); setIsOpen(true); setIsExpanded(false); } },
    { id: 'tradetron', icon: '🔗', label: 'TradetrON', action: () => { setInputValue('How to set up TradetrON?'); setIsOpen(true); setIsExpanded(false); } },
  ];

  // Toggle FAB expansion (mobile only)
  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div className={`copilot-container ${isOpen ? 'open' : ''} ${isMobile ? 'mobile' : 'desktop'}`}>
      {/* Expanded Quick Actions Menu (Mobile FAB) */}
      {isMobile && isExpanded && (
        <div className="fab-menu">
          {quickActions.map((action, index) => (
            <button
              key={action.id}
              className="fab-action"
              onClick={action.action}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <span className="fab-action-icon">{action.icon}</span>
              <span className="fab-action-label">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Main Toggle Button */}
      <button 
        className={`copilot-toggle ${isExpanded ? 'expanded' : ''}`}
        onClick={isMobile ? toggleExpand : () => setIsOpen(!isOpen)}
        aria-label="Toggle AI Copilot"
      >
        {isOpen && !isMobile ? (
          <span className="toggle-icon">✕</span>
        ) : isMobile && isExpanded ? (
          <span className="toggle-icon">✕</span>
        ) : (
          <>
            <span className="toggle-icon">🤖</span>
            <span className="toggle-badge">AI</span>
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="copilot-window">
          {/* Header */}
          <div className="copilot-header">
            <div className="header-info">
              <span className="header-icon">🤖</span>
              <div className="header-text">
                <h3>ZO Bull AI Copilot</h3>
                <span className="status">Online • Supports EN/HI/hinglish</span>
              </div>
            </div>
            <div className="header-actions">
              <button onClick={clearChat} className="action-btn" title="Clear Chat">
                🗑️
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="copilot-messages">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message ${message.role} ${message.isStreaming ? 'streaming' : ''}`}
              >
                <div className="message-avatar">
                  {message.role === 'assistant' ? '🤖' : '👤'}
                </div>
                <div className="message-content">
                  <div className="message-text">
                    {message.content.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                  <div className="message-meta">
                    <span className="timestamp">{formatTime(message.timestamp)}</span>
                    {message.isStreaming && <span className="streaming-indicator">...</span>}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <button onClick={() => setInputValue('Explain RSI indicator')}>
              📊 RSI
            </button>
            <button onClick={() => setInputValue('Supertrend kya hai?')}>
              📈 Supertrend
            </button>
            <button onClick={() => setInputValue('Create a momentum strategy')}>
              ⚡ Strategy
            </button>
          </div>

          {/* Input */}
          <div className="copilot-input">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about trading... (42 languages supported)"
              disabled={isLoading}
            />
            <button 
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
              className="send-btn"
            >
              {isLoading ? '⏳' : '➤'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CopilotChat;